export type LiveSummaryPositionInput = {
  averagePrice: number;
  quantity: number;
};

export type LiveSummaryPositionSnapshot = {
  investedAmount: number;
  marketValue: number;
  unrealizedPnl: number;
  unrealizedPnlPct: number;
};

export type LiveSummaryReductionSimulation = {
  requestedReductionPct: number;
  effectiveReductionPct: number;
  reductionQuantity: number;
  remainingQuantity: number;
  estimatedProceeds: number;
  estimatedRealizedPnl: number;
};

export const POSITION_COPY = {
  title: '내 계좌 영향',
  simulationTitle: '수량 시뮬레이션',
  simulationDescription: '사용자가 매도 가정 비율을 직접 입력하면 저장된 보유 수량을 기준으로 예상 수량·금액을 계산합니다.',
  legalNotice: '평가손익과 시뮬레이션은 저장된 보유정보와 현재가, 사용자가 직접 입력한 가정을 기준으로 한 계산 정보이며 특정 거래를 권고하거나 지시하지 않습니다.',
} as const;

function finitePositive(value: unknown): number | null {
  const parsed = typeof value === 'number'
      ? value
      : Number(String(value ?? '').replace(/,/g, '').trim());
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function finiteAbsolutePrice(value: unknown): number | null {
  const parsed = typeof value === 'number'
      ? value
      : Number(String(value ?? '').replace(/,/g, '').trim());
  if (!Number.isFinite(parsed) || parsed === 0) return null;
  return Math.abs(parsed);
}

export function buildPortfolioPosition(
    averagePrice: unknown,
    quantity: unknown,
): LiveSummaryPositionInput | null {
  const normalizedAveragePrice = finitePositive(averagePrice);
  const normalizedQuantity = finitePositive(quantity);
  if (normalizedAveragePrice === null || normalizedQuantity === null) return null;

  return {
    averagePrice: normalizedAveragePrice,
    quantity: normalizedQuantity,
  };
}

export function buildPositionSnapshot(
    input: LiveSummaryPositionInput,
    currentPrice: number | null,
): LiveSummaryPositionSnapshot | null {
  const normalizedCurrentPrice = finiteAbsolutePrice(currentPrice);
  if (normalizedCurrentPrice === null) return null;

  const investedAmount = input.averagePrice * input.quantity;
  const marketValue = normalizedCurrentPrice * input.quantity;
  const unrealizedPnl = marketValue - investedAmount;
  const unrealizedPnlPct = investedAmount > 0 ? (unrealizedPnl / investedAmount) * 100 : 0;

  return {
    investedAmount,
    marketValue,
    unrealizedPnl,
    unrealizedPnlPct,
  };
}

export function buildReductionSimulation(
    input: LiveSummaryPositionInput,
    currentPrice: number | null,
    reductionPct: number | null,
): LiveSummaryReductionSimulation | null {
  const normalizedCurrentPrice = finiteAbsolutePrice(currentPrice);
  if (
      normalizedCurrentPrice === null
      || reductionPct === null
      || !Number.isFinite(reductionPct)
      || reductionPct <= 0
      || reductionPct > 100
  ) return null;

  const rawQuantity = input.quantity * (reductionPct / 100);
  const wholeShareHolding = Number.isInteger(input.quantity);

  // 국내 주식처럼 정수 수량으로 보유한 경우 입력 비율과 가장 가까운
  // 실제 주문 가능 수량으로 환산한다. 0주가 되어 결과가 사라지는 것을 막는다.
  const reductionQuantity = wholeShareHolding
      ? Math.min(input.quantity, Math.max(1, Math.round(rawQuantity)))
      : Math.min(input.quantity, Math.max(0.000001, Math.round(rawQuantity * 1_000_000) / 1_000_000));

  if (reductionQuantity <= 0) return null;

  const remainingQuantity = Math.max(0, input.quantity - reductionQuantity);
  const effectiveReductionPct = input.quantity > 0
      ? (reductionQuantity / input.quantity) * 100
      : 0;
  const estimatedProceeds = normalizedCurrentPrice * reductionQuantity;
  const estimatedRealizedPnl = (normalizedCurrentPrice - input.averagePrice) * reductionQuantity;

  return {
    requestedReductionPct: reductionPct,
    effectiveReductionPct,
    reductionQuantity,
    remainingQuantity,
    estimatedProceeds,
    estimatedRealizedPnl,
  };
}
